import React, { useMemo, useState } from 'react'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts'
import { useMediaQuery, Box } from '@mui/material'
import { formatNumberChart } from 'utils/formatNumber'
import { BarChartProps, InputDataProps } from 'types'
import { BAR_CHART_DOMAIN_RANGES, BAR_CHART_TICK_RANGES } from 'constant'

export default function CommonBarChart({
  data,
  height,
  barColor = '#4e82b8',
  activeBarColor = '#4e82b8',
  barWidth = 30,
  axisFontSize = 14,
  isGrid = false,
  gridBackground = '#fff',
  gridColor = '#f5f5f5',
  isTickLine = false,
  tickColor = '#000',
  tickCount = 6,
  showPercentage = false,
}: BarChartProps) {
  const isSmaller600 = useMediaQuery('(max-width:600px)')

  const reversedData: InputDataProps[] = !!data && data?.slice().reverse()

  const [activeTooltip, setActiveTooltip] = useState<boolean>(false)
  const [hoveredBarIndex, setHoveredBarIndex] = useState<number | null>(null)

  const calcDomain = useMemo(() => {
    if (!data?.length) return undefined
    const maxValue = Math.max(...data.map((ele) => ele.value))
    const minValue = Math.min(...data.map((ele) => ele.value))
    for (const range of BAR_CHART_DOMAIN_RANGES) {
      if (maxValue <= range) {
        return [0, range]
      }
    }
    return undefined
  }, [data])

  const calcTicks = useMemo(() => {
    if (!data?.length) return undefined
    const maxValue = Math.max(...data.map((ele) => ele.value))
    for (const range of BAR_CHART_TICK_RANGES(isSmaller600)) {
      if (maxValue <= range.max) {
        return range.ticks
      }
    }
    return undefined
  }, [data, isSmaller600])

  function CustomTooltip(props: any) {
    const { active, payload } = props
    if (active && payload && payload.length) {
      return (
        <>
          {activeTooltip && (
            <Box
              sx={{ background: '#fff', padding: '6px 12px', border: `1px solid ${barColor}`, borderRadius: '12px' }}
            >
              <span>
                {showPercentage ? `${payload[0].value.toFixed(2)}%` : `${formatNumberChart(payload[0].value, 2, true)}`}
              </span>
            </Box>
          )}
        </>
      )
    }
    return null
  }
  return (
    <Box sx={{ position: 'relative', left: { xs: '-18px', sm: '-6px' } }}>
      <ResponsiveContainer width='100%' height={isSmaller600 ? 450 : height}>
        <BarChart
          data={isSmaller600 ? reversedData : data}
          layout={isSmaller600 ? 'vertical' : 'horizontal'}
          margin={{
            right: isSmaller600 ? 0 : 30,
            left: isSmaller600 ? 0 : 20,
            bottom: 5,
          }}
          barSize={barWidth}
        >
          {isGrid && <CartesianGrid strokeDasharray='0 0' stroke={gridColor} fill={gridBackground} />}
          <XAxis
            type={isSmaller600 ? 'number' : 'category'}
            dataKey={isSmaller600 ? 'value' : 'year'}
            scale={isSmaller600 ? 'auto' : 'point'}
            padding={{
              left: isSmaller600 ? 0 : barWidth * 1.22,
              right: isSmaller600 ? 0 : barWidth * 1.2,
            }}
            tickFormatter={(value) =>
              !isSmaller600
                ? value
                : showPercentage
                ? `${value}%`
                : value < 1
                ? `${value}`
                : formatNumberChart(value, 2)
            }
            tickLine={isTickLine}
            tick={{ fontSize: axisFontSize, fontWeight: 500, fill: tickColor }}
            tickCount={tickCount}
            ticks={!isSmaller600 ? undefined : calcTicks}
            domain={!isSmaller600 ? undefined : calcDomain}
          />
          <YAxis
            type={isSmaller600 ? 'category' : 'number'}
            dataKey={isSmaller600 ? 'year' : 'value'}
            scale={isSmaller600 ? 'point' : 'auto'}
            padding={{ top: !isSmaller600 ? 0 : barWidth / 1.5, bottom: !isSmaller600 ? 0 : barWidth / 1.5 }}
            tickFormatter={(value) =>
              isSmaller600 ? value : showPercentage ? `${value}%` : value < 1 ? `${value}` : formatNumberChart(value, 2)
            }
            tickLine={isTickLine}
            tick={{ fontSize: axisFontSize, fontWeight: 500, fill: tickColor }}
            tickCount={isSmaller600 ? 6 : undefined}
            ticks={isSmaller600 ? undefined : calcTicks}
            domain={isSmaller600 ? undefined : calcDomain}
          />
          <Tooltip content={<CustomTooltip />} />
          <Bar
            dataKey='value'
            fill={barColor}
            activeBar={{ fill: hoveredBarIndex ? activeBarColor : barColor }}
            background={{ fill: 'none' }}
            onMouseEnter={(entry, index) => {
              setActiveTooltip(true)
              setHoveredBarIndex(index + 1)
            }}
            onMouseLeave={() => {
              setActiveTooltip(false)
              setHoveredBarIndex(null)
            }}
          />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  )
}
